<template>
  <b-modal
    id="modal-bundle-service-import"
    :title="!importSuccess ? 'Import dịch vụ' : 'Kết quả import'"
    :size="!importSuccess ? 'md' : 'xl'"
    centered
    hide-footer
    @hidden="resetModal"
  >
    <b-overlay :show="loading">
      <form @submit.prevent="handleSubmit">
        <!-- Upload file -->
        <div v-if="!importSuccess" class="py-4">
          <label>Import file</label>
          <div class="wrap__file mb-1">
            <span class="file__name">
              {{ file ? fileName : 'file import.xlsx' }}
            </span>
            <b-icon-upload
              scale="1.5"
              class="cursor-pointer"
              @click="$refs.fileUpload.click()"
            />
            <input
              v-show="false"
              type="file"
              id="bundle-service-file-upload"
              ref="fileUpload"
              accept=".xlsx, .xls"
              @change="onUploadFile"
            />
          </div>
          <span v-if="showFileSizeError" class="text-danger">
            Kích thước file import tối đa nhỏ hơn 1MB
          </span>
        </div>

        <!-- Kết quả import -->
        <div v-else>
          <b-row class="mb-4">
            <b-col cols="4" class="d-flex align-items-center">
              <div class="wrap__icon mr-3">
                <i class="fas fa-hospital text-primary" style="font-size:1.2rem;" />
              </div>
              <div>
                <h6 class="mb-0">Tổng bản ghi</h6>
                <p class="mb-0 font-weight-bold">{{ paging.total }}</p>
              </div>
            </b-col>
            <b-col cols="4" class="d-flex align-items-center">
              <div class="wrap__icon mr-3">
                <i class="fas fa-check-circle text-success" style="font-size:1.2rem;" />
              </div>
              <div>
                <h6 class="mb-0">Thành công</h6>
                <p class="mb-0 font-weight-bold text-success">{{ totalSuccess }}</p>
              </div>
            </b-col>
            <b-col cols="4" class="d-flex align-items-center">
              <div class="wrap__icon mr-3">
                <i class="fas fa-times-circle text-danger" style="font-size:1.2rem;" />
              </div>
              <div>
                <h6 class="mb-0">Thất bại</h6>
                <p class="mb-0 font-weight-bold text-danger">{{ totalFail }}</p>
              </div>
            </b-col>
          </b-row>

          <!-- Result table -->
          <div class="table-responsive">
            <table class="table table-bordered table-sm">
              <thead class="thead-light">
                <tr>
                  <th>STT</th>
                  <th>Tên dịch vụ</th>
                  <th>Thời gian (phút)</th>
                  <th>Nhà cung cấp</th>
                  <th class="text-right">Giá</th>
                  <th>Mô tả</th>
                  <th class="text-center">Trạng thái</th>
                  <th>Chi tiết lỗi</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in pagedResults" :key="idx">
                  <td>{{ (paging.page - 1) * paging.pageSize + idx + 1 }}</td>
                  <td>{{ item.name || '—' }}</td>
                  <td>{{ item.minutes || '—' }}</td>
                  <td>{{ providerName(item.serviceProviderId) }}</td>
                  <td class="text-right">{{ formatCurrency(item.price) }}</td>
                  <td>{{ item.description || '—' }}</td>
                  <td class="text-center">
                    <div class="status" :class="item.transactionStatus ? 'active' : 'inactive'">
                      {{ item.transactionStatus ? 'Thành công' : 'Lỗi' }}
                    </div>
                  </td>
                  <td style="min-width:200px;">
                    <div
                      v-for="(err, i) in item.errorMessages"
                      :key="i"
                      class="text-danger"
                      style="font-size:0.82rem;"
                    >- {{ err }}</div>
                  </td>
                </tr>
                <tr v-if="!pagedResults.length">
                  <td colspan="8" class="text-center text-muted py-4">Không có dữ liệu</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="paging.total > paging.pageSize" class="d-flex justify-content-end mt-2">
            <b-pagination
              v-model="paging.page"
              :total-rows="paging.total"
              :per-page="paging.pageSize"
              size="sm"
            />
          </div>
        </div>

        <!-- Footer buttons -->
        <div v-if="!importSuccess" class="d-flex justify-content-end align-items-center mt-4">
          <b-button class="btn mr-2" type="button" @click="onClickCancel">Hủy</b-button>
          <b-button
            class="btn btn-success ml-3"
            type="submit"
            :disabled="disableBtnImport || loading"
          >
            <i class="fas fa-file-import mr-1" />
            Import
          </b-button>
        </div>

        <div v-else class="d-flex justify-content-end mt-4">
          <b-button class="btn btn-success" type="button" @click="onClickCancel">Đóng</b-button>
        </div>
      </form>
    </b-overlay>
  </b-modal>
</template>

<script>
import { EXCEL_FILE_TYPE } from '@/core/plugins/constants';

export default {
  name: 'ServiceImport',
  props: {
    providers: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      importSuccess: false,
      file: null,
      showFileSizeError: false,
      allResults: [],
      paging: { total: 0, page: 1, pageSize: 10 },
      totalSuccess: 0,
      totalFail: 0,
    };
  },
  computed: {
    fileName() {
      return this.file?.name || '';
    },
    disableBtnImport() {
      return !this.file || this.showFileSizeError;
    },
    pagedResults() {
      const { page, pageSize } = this.paging;
      return this.allResults.slice((page - 1) * pageSize, page * pageSize);
    },
  },
  methods: {
    onUploadFile(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (!EXCEL_FILE_TYPE.includes(file.type)) {
        this.$refs.fileUpload.value = null;
        this.$toastr?.e?.({ title: 'Lỗi', msg: 'Chỉ hỗ trợ file .xlsx hoặc .xls' });
        return;
      }

      if (file.size > 1024000) {
        this.showFileSizeError = true;
        return;
      }

      this.file = file;
      this.showFileSizeError = false;
    },

    async handleSubmit() {
      if (this.disableBtnImport) return;
      try {
        this.loading = true;
        const formData = new FormData();
        formData.append('importFile', this.file);

        const { data } = await this.$api.post(
          'BundleService/ImportBundleService',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );

        const rows = Array.isArray(data?.data) ? data.data : [];
        this.allResults = rows.map((el) => ({
          ...el,
          errorMessages: this.parseErrorMessages(el.errorMessage),
        }));
        this.paging.total = data?.total ?? rows.length;
        this.totalSuccess = data?.totalSuccess ?? rows.filter((r) => r.transactionStatus).length;
        this.totalFail = data?.totalFail ?? rows.filter((r) => !r.transactionStatus).length;
        this.paging.page = 1;
        this.importSuccess = true;
      } catch (err) {
        this.$toastr?.e?.({ title: 'Lỗi!', msg: err?.message || 'Import thất bại' });
      } finally {
        this.loading = false;
      }
    },

    parseErrorMessages(text) {
      if (!text) return [];
      return text.split('\n').filter((s) => s.trim());
    },

    providerName(providerId) {
      if (!providerId) return '—';
      const provider = this.providers.find((p) => p.value === providerId);
      return provider ? provider.text : providerId;
    },

    formatCurrency(value) {
      if (!value) return '0 VNĐ';
      return new Intl.NumberFormat('vi-VN').format(value) + ' VNĐ';
    },

    resetModal() {
      if (this.importSuccess) {
        this.$emit('import-success');
      }
      this.file = null;
      this.showFileSizeError = false;
      this.importSuccess = false;
      this.allResults = [];
      this.paging = { total: 0, page: 1, pageSize: 10 };
      this.totalSuccess = 0;
      this.totalFail = 0;
      if (this.$refs.fileUpload) this.$refs.fileUpload.value = null;
    },

    onClickCancel() {
      this.$bvModal.hide('modal-bundle-service-import');
    },
  },
};
</script>

<style lang="scss" scoped>
.wrap__file {
  padding: 10px 16px;
  border: 1px solid #dddddd;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  &:hover {
    border-color: #aaa;
  }

  .file__name {
    color: #5e6278;
    font-size: 0.9rem;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.wrap__icon {
  background: #e4f5f5;
  border-radius: 8px;
  width: 44px;
  height: 44px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
}

.status {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  min-width: 90px;

  &.active {
    background: #21a567;
  }

  &.inactive {
    background: #ff5756;
  }
}
</style>

<style lang="scss">
#modal-bundle-service-import {
  .modal-dialog.modal-xl {
    max-width: 1400px;
  }
}
</style>
